// Flows route — '/flows'.
//
// Auth gate:
//   - `beforeLoad` reads the token directly from useAuthStore.getState()
//     (outside the React tree) and throws redirect to /login if missing.
//     Mirrors the pattern in account.tsx.
//
// View state machine:
//   - 'list'    → FlowList (fetch + render flow grid)
//   - 'builder' → FlowBuilder canvas editor
//
// State vocabulary (shared with the admin overview and Streams page via
// components/flows/flow-state.ts): Idle / Armed / On air. Only a flow whose
// selected output is receiving a signal is "On air"; an activated flow with
// no signal is "Armed". Arming and disarming change what viewers can see,
// so both go through the shared ConfirmDialog and are never optimistic.
//
// NOTE: AppShell is applied globally by __root.tsx — do NOT import or wrap it here.
import { useMemo, useRef, useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Loader2,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  RotateCw,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'

import { FlowBuilder } from '@/components/flows/FlowBuilder'
import { FlowMiniMap } from '@/components/flows/FlowMiniMap'
import { FlowStatePill } from '@/components/flows/FlowStatePill'
import {
  activationCopy,
  getFlowState,
  onAirFlowNames,
  type ActivationIntent,
} from '@/components/flows/flow-state'
import { getFlowOverviewVisual } from '@/components/flows/node-definitions'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { InlineError, InlineEmpty } from '@/components/ui'
import { apiClient } from '@/lib/api-client'
import { parseGraph, formatDate } from '@/lib/flow-graph'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import type {
  FlowListItem,
  FlowSingle,
  FlowsListResponse,
  FlowMutationResponse,
} from '@/lib/types'

// Shared focus ring for menu items so keyboard focus is unmistakable on the
// dark theme (the base item only tints the background).
const MENU_ITEM_CLASS =
  'gap-2 px-2.5 py-2 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-cyber-cyan/60'
// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/flows')({
  validateSearch: (search: Record<string, unknown>): { id?: number; view?: 'layout' | 'nodes'; new?: boolean } => {
    const id = Number(search.id)
    return {
      ...(Number.isSafeInteger(id) && id > 0 ? { id } : {}),
      ...(search.view === 'nodes' || search.view === 'layout' ? { view: search.view } : {}),
      ...(search.new === true || search.new === 'true' ? { new: true } : {}),
    }
  },
  beforeLoad: () => {
    if (!useAuthStore.getState().token) {
      throw redirect({ to: '/login', replace: true })
    }
  },
  component: FlowsPage,
})

// ---------------------------------------------------------------------------
// Top-level page — view state machine
// ---------------------------------------------------------------------------

function FlowsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const flow = useMemo(() => search.id ? { ID: search.id } : null, [search.id])
  const openNew = () => void navigate({ search: { new: true, view: 'layout' } })

  if (search.id || search.new) {
    return (
      <div className="h-[calc(100dvh-3.5rem)] w-full overflow-hidden">
        <FlowBuilder flow={flow} view={search.view ?? 'layout'}
          onViewChange={view => void navigate({ search: current => ({ ...current, view }), replace: true })}
          onSaved={id => void navigate({ search: current => ({ id, view: current.view ?? 'layout' }), replace: true })}
          onBack={() => void navigate({ search: {} })} />
      </div>
    )
  }
  return (
    <main className="mx-auto max-w-[1536px] px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Flows</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sources, scenes and signal paths. Open any flow in Layers or Nodes.</p>
        </div>
        <Button onClick={openNew}>New flow</Button>
      </div>
      <FlowList onNew={openNew} onEdit={f => void navigate({ search: { id: f.ID, view: 'layout' } })} />
    </main>
  )
}

// ---------------------------------------------------------------------------
// FlowList
// ---------------------------------------------------------------------------

interface FlowListProps {
  onNew: () => void
  onEdit: (flow: FlowSingle) => void
}

function FlowList({ onNew, onEdit }: FlowListProps) {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['flows'],
    queryFn: () => apiClient.get<FlowsListResponse>('/flows/'),
    refetchInterval: 5000,
  })

  const flows: FlowListItem[] = data?.flows ?? []

  if (isLoading) {
    return (
      <div
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        aria-busy="true"
        aria-live="polite"
      >
        <span className="sr-only">Loading flows…</span>
        {[0, 1, 2].map((i) => (
          <Card key={i} className="min-h-[188px]" aria-hidden="true">
            <CardHeader className="space-y-3 pb-3">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-24 w-full rounded-md" />
              <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-24" />
                <Skeleton className="ml-auto h-9 w-9" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <InlineError message="Couldn't load flows. Check your connection and try again." />
        <Button
          size="sm"
          variant="outline"
          onClick={() => void refetch()}
          disabled={isFetching}
          aria-busy={isFetching}
        >
          {isFetching ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          ) : (
            <RotateCw aria-hidden="true" className="size-4" />
          )}
          {isFetching ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    )
  }

  if (flows.length === 0) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-16 text-center">
        <InlineEmpty message="No flows yet. A flow connects an encoder source to a Main Output, with an optional mixer and effects in between." />
        <Button onClick={onNew}>New flow</Button>
      </div>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {flows.map((flow) => (
        <FlowCard key={flow.ID} flow={flow} allFlows={flows} onEdit={onEdit} />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// FlowCard
// ---------------------------------------------------------------------------

interface FlowCardProps {
  flow: FlowListItem
  allFlows: FlowListItem[]
  onEdit: (flow: FlowSingle) => void
}

function FlowOverviewIcon({
  icon,
  iconUrl,
  color,
  label,
}: {
  icon: string
  iconUrl?: string
  color: string
  label: string
}) {
  return (
    <span
      aria-label={label}
      className="flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-md border border-border/40 bg-background/70 text-lg leading-none"
      style={{ color, borderColor: `${color}66` }}
    >
      {iconUrl ? (
        <img src={iconUrl} alt="" aria-hidden="true" className="h-5 w-5 object-contain" />
      ) : (
        icon
      )}
    </span>
  )
}

function FlowCard({ flow, allFlows, onEdit }: FlowCardProps) {
  const queryClient = useQueryClient()
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [confirmIntent, setConfirmIntent] = useState<ActivationIntent | null>(null)

  const graph = parseGraph(flow.GRAPH)
  const visual = getFlowOverviewVisual(graph.nodes)

  const state = getFlowState(flow)
  const isActive = state !== 'idle'
  const isOnAir = state === 'on-air'

  // The builder loads the full record by id; the list item carries enough
  // to open it (ID, NAME, GRAPH, ACTIVE).
  const openInBuilder = () => onEdit(flow as unknown as FlowSingle)

  // Never optimistic: arming or disarming changes what can reach viewers,
  // so the card shows the server's answer, not a guess.
  const toggleMutation = useMutation({
    mutationFn: (intent: ActivationIntent) =>
      apiClient.post<FlowMutationResponse>('/flows/', {
        action: intent,
        id: flow.ID,
      }),
    onSuccess: async (data, intent) => {
      if (data && data.success === false) {
        throw new Error(data.message ?? 'The server refused the change.')
      }
      await queryClient.invalidateQueries({ queryKey: ['flows'] })
      setConfirmIntent(null)
      toast.success(
        intent === 'activate'
          ? `${flow.NAME} is armed. It goes on air when a source publishes.`
          : isOnAir
            ? `${flow.NAME} is off air.`
            : `${flow.NAME} is disarmed.`,
      )
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['flows'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiClient.post<FlowMutationResponse>('/flows/', {
        action: 'delete',
        id: flow.ID,
      }),
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['flows'] })
        toast.success('Flow deleted')
      }
    },
    onError: () => toast.error('Delete failed'),
  })

  // Menu → dialog hand-off. Opening the dialog from inside a menu item's
  // select handler races the menu's own close/focus-restore, so the request
  // is parked here and the dialog opens once the menu has fully closed.
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const pendingDeleteRef = useRef(false)
  const pendingToggleRef = useRef(false)

  const isToggling = toggleMutation.isPending
  const isDeleting = deleteMutation.isPending
  const toggleIntent: ActivationIntent = isActive ? 'deactivate' : 'activate'
  const toggleLabel = isActive ? (isOnAir ? 'Take off air' : 'Disarm') : 'Arm'
  const ToggleIcon = isActive ? PowerOff : Power

  const confirmCopy =
    confirmIntent === null
      ? null
      : activationCopy(confirmIntent, flow.NAME, {
          connected: isOnAir,
          otherOnAir: onAirFlowNames(allFlows, flow.ID),
        })

  const openDeleteDialog = () => {
    deleteMutation.reset()
    setDeleteOpen(true)
  }

  const openToggleDialog = () => {
    toggleMutation.reset()
    setConfirmIntent(toggleIntent)
  }

  const confirmDelete = () => {
    if (isDeleting) return
    deleteMutation.mutate(undefined, {
      onSuccess: () => setDeleteOpen(false),
    })
  }

  return (
    <>
      <Card
        variant={isOnAir ? 'flow' : undefined}
        aria-busy={isDeleting || isToggling}
        className={cn(
          'flex h-full flex-col transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5',
          isOnAir && 'border-cyber-green/60 shadow-card-live-glow',
          state === 'armed' && 'border-cyber-cyan/40',
          isDeleting && 'pointer-events-none opacity-60',
        )}
      >
        <CardHeader className="space-y-2 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <FlowOverviewIcon
                icon={visual.icon}
                iconUrl={visual.iconUrl}
                color={visual.color}
                label={visual.label}
              />
              <div className="min-w-0">
                <CardTitle className="truncate text-sm font-semibold" title={flow.NAME}>
                  {flow.NAME}
                </CardTitle>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                  <span>Updated {formatDate(flow.UPDATED_AT)}</span>
                  <span aria-hidden="true">·</span>
                  <span>
                    {graph.nodes.length} node{graph.nodes.length === 1 ? '' : 's'} ·{' '}
                    {graph.connections.length} connection{graph.connections.length === 1 ? '' : 's'}
                  </span>
                </div>
              </div>
            </div>
            <FlowStatePill state={state} />
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3 pt-0">
          <FlowMiniMap
            nodes={graph.nodes}
            conns={graph.connections}
            isActive={isOnAir}
            className="h-24 rounded-md"
          />
          <div className="mt-auto border-t border-border/50 pt-3">
            <div className="flex flex-wrap items-center gap-2">
              {/* Primary action */}
              <Button
                size="sm"
                variant="outline"
                className="min-w-0 flex-1 sm:flex-none"
                onClick={openInBuilder}
                aria-label={`Open ${flow.NAME} in builder`}
              >
                <Pencil aria-hidden="true" className="size-4 shrink-0" />
                Open
              </Button>

              {/* State action — visible because it changes what is on air */}
              <Button
                size="sm"
                variant={isActive ? 'ghost' : 'outline'}
                className={cn(
                  'min-w-0 flex-1 sm:flex-none',
                  isActive && 'text-muted-foreground hover:text-cyber-magenta',
                )}
                onClick={openToggleDialog}
                disabled={isToggling}
                aria-busy={isToggling}
              >
                {isToggling ? (
                  <Loader2 aria-hidden="true" className="size-4 shrink-0 animate-spin" />
                ) : (
                  <ToggleIcon aria-hidden="true" className="size-4 shrink-0" />
                )}
                {toggleLabel}
              </Button>

              {/* Secondary + destructive actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    ref={menuTriggerRef}
                    size="icon"
                    variant="ghost"
                    className="ml-auto h-9 w-9 shrink-0 text-muted-foreground data-[state=open]:bg-cyber-cyan/10 data-[state=open]:text-cyber-cyan"
                    aria-label={`More actions for ${flow.NAME}`}
                  >
                    <MoreHorizontal aria-hidden="true" className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 border-border bg-popover"
                  onCloseAutoFocus={(event) => {
                    if (pendingDeleteRef.current) {
                      pendingDeleteRef.current = false
                      event.preventDefault()
                      openDeleteDialog()
                    } else if (pendingToggleRef.current) {
                      pendingToggleRef.current = false
                      event.preventDefault()
                      openToggleDialog()
                    }
                  }}
                >
                  <DropdownMenuLabel className="truncate px-2.5 text-xs font-medium text-muted-foreground">
                    {flow.NAME}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className={MENU_ITEM_CLASS} onSelect={openInBuilder}>
                    <Pencil aria-hidden="true" className="size-4 shrink-0" />
                    Open in builder
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className={MENU_ITEM_CLASS}
                    disabled={isToggling}
                    onSelect={() => {
                      pendingToggleRef.current = true
                    }}
                  >
                    <ToggleIcon aria-hidden="true" className="size-4 shrink-0" />
                    {toggleLabel}…
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className={cn(
                      MENU_ITEM_CLASS,
                      'text-cyber-magenta focus:bg-cyber-magenta/10 focus:text-cyber-magenta focus-visible:ring-cyber-magenta/60',
                    )}
                    disabled={isDeleting}
                    onSelect={() => {
                      pendingDeleteRef.current = true
                    }}
                  >
                    {isDeleting ? (
                      <Loader2 aria-hidden="true" className="size-4 shrink-0 animate-spin" />
                    ) : (
                      <Trash2 aria-hidden="true" className="size-4 shrink-0" />
                    )}
                    {isDeleting ? 'Deleting…' : 'Delete flow…'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Arm / disarm confirmation ── */}
      {confirmCopy ? (
        <ConfirmDialog
          open={confirmIntent !== null}
          onOpenChange={(open) => {
            if (!open) setConfirmIntent(null)
          }}
          title={confirmCopy.title}
          description={confirmCopy.description}
          confirmLabel={confirmCopy.confirmLabel}
          busyLabel={confirmCopy.busyLabel}
          confirmVariant={confirmIntent === 'activate' ? 'default' : 'cyber-magenta'}
          busy={isToggling}
          error={
            toggleMutation.isError
              ? toggleMutation.error instanceof Error
                ? toggleMutation.error.message
                : 'The change did not go through. Try again.'
              : null
          }
          onConfirm={() => {
            if (confirmIntent) toggleMutation.mutate(confirmIntent)
          }}
          returnFocusTo={menuTriggerRef}
          data-testid="flow-activation-confirm"
        />
      ) : null}

      {/* ── Delete confirmation ── */}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          // Keep the dialog up while the request is in flight so the user
          // sees the outcome; Cancel/Escape work again once it settles.
          if (!isDeleting) setDeleteOpen(open)
        }}
        title="Delete this flow?"
        description={
          <>
            &ldquo;{flow.NAME}&rdquo; and its saved graph will be permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        busyLabel="Deleting…"
        confirmVariant="cyber-magenta"
        busy={isDeleting}
        error={deleteMutation.isError ? 'Delete failed. The flow was not removed. Try again.' : null}
        onConfirm={confirmDelete}
        returnFocusTo={menuTriggerRef}
      />
    </>
  )
}
